import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronRight, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MaskLines, Reveal } from "@/components/common/Reveal";
import { Seo, breadcrumbLd } from "@/components/common/Seo";

export const Section = ({ children, className = "", dark = false, id, testid }) => (
  <section id={id} data-testid={testid} className={`${dark ? "on-dark bg-ink text-white" : ""} py-20 sm:py-24 lg:py-32 ${className}`}>
    <div className="container-x">{children}</div>
  </section>
);

export const SectionHead = ({ eyebrow, title, lede, className = "", align = "left" }) => (
  <Reveal className={`mb-12 max-w-3xl lg:mb-16 ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
    {eyebrow && <p className="eyebrow mb-5"><span className="h-px w-6 bg-current" aria-hidden />{eyebrow}</p>}
    <h2 className="h-section">{title}</h2>
    {lede && <p className="lede mt-5">{lede}</p>}
  </Reveal>
);

export const Crumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="mb-8" data-testid="breadcrumbs">
    <ol className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-slate-300">
      {items.map((it, i) => (
        <li key={it.to} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden />}
          {i < items.length - 1 ? <Link to={it.to} className="hover:text-teal-bright">{it.label}</Link> : <span aria-current="page" className="text-white">{it.label}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

const heroImages = [
  ["/services/cloud", "infra"], ["/services/reliability", "infra"], ["/services", "security"], ["/pricing", "reports"], ["/industries", "review"],
  ["/compliance", "globe"], ["/reviews", "partners"], ["/sample-report", "reports"],
  ["/methodology", "review"], ["/how-it-works", "review"], ["/partnerships", "partners"], ["/about", "about"], ["/contact", "contact"],
  ["/request-assessment", "contact"], ["/faq", "contact"],
];
const heroAlts = {
  infra: "Server infrastructure and cloud systems being reviewed by an engineer",
  security: "Security analyst testing an application for vulnerabilities",
  reports: "Assessment report with findings and charts on a desk",
  review: "Team reviewing a digital product assessment together",
  globe: "Digital globe showing international network connections",
  partners: "Business partners shaking hands across a table",
  about: "Rigorfield assessment team at work",
  contact: "Support specialist ready to answer a customer enquiry",
};
const imageFor = (path) => heroImages.find(([p]) => path.startsWith(p))?.[1];

// Page hero used by every inner page. Also emits SEO + breadcrumb JSON-LD.
export const PageHero = ({ seo, crumbs, eyebrow, title, lede, children, jsonLd = [] }) => {
  const items = [{ label: "Home", to: "/" }, ...crumbs];
  const img = imageFor(seo.path || "");
  return (
    <>
      <Seo {...seo} jsonLd={[breadcrumbLd(items), ...jsonLd]} />
      <section className="on-dark relative overflow-hidden bg-ink pb-14 pt-28 text-white sm:pb-20 lg:pb-24 lg:pt-36">
        <div className="absolute -right-40 top-0 h-[480px] w-[480px] rounded-full bg-teal/25 blur-[120px]" aria-hidden />
        <div className={`container-x relative ${img ? "grid items-center gap-10 lg:grid-cols-12" : ""}`}>
          <div className={img ? "min-w-0 lg:col-span-7" : ""}>
            <Crumbs items={items} />
            {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
            <MaskLines as="h1" lines={Array.isArray(title) ? title : [title]} testid="page-title" className="max-w-5xl text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-[3.4rem]" />
            {lede && <p className="lede mt-6 max-w-2xl">{lede}</p>}
            {children && <div className="mt-8">{children}</div>}
          </div>
          {img && (
            <div className="lg:col-span-5">
              <img src={`/img/${img}.webp`} alt={heroAlts[img] || ""} width="1400" height="934" decoding="async" className="aspect-[16/10] w-full rounded-2xl border border-white/15 object-cover shadow-[0_30px_70px_-30px_rgba(0,0,0,.7)]" data-testid="page-hero-image" />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const sevStyles = {
  Critical: "bg-red-50 text-red-800 border-red-200",
  High: "bg-orange-50 text-orange-800 border-orange-200",
  Medium: "bg-yellow-50 text-yellow-900 border-yellow-200",
  Low: "bg-green-50 text-green-800 border-green-200",
  Informational: "bg-slate-50 text-slate-700 border-slate-200",
};
export const Sev = ({ level }) => (
  <span className={`inline-flex min-w-[88px] items-center justify-center rounded-sm border px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider ${sevStyles[level]}`}>{level}</span>
);

export const ArrowLink = ({ to, children, className = "", testid }) => (
  <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-2 text-sm font-semibold text-teal ${className}`}>
    <span className="link-u">{children}</span>
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden />
  </Link>
);

export const ExtLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-teal link-u">
    {children}<ArrowUpRight className="h-3.5 w-3.5" aria-hidden /><span className="sr-only">(opens in new tab)</span>
  </a>
);

export const FaqList = ({ faqs, idPrefix = "faq" }) => (
  <Accordion type="single" collapsible className="border-t border-ink/15" data-testid={`${idPrefix}-list`}>
    {faqs.map((f, i) => (
      <AccordionItem key={i} value={`${idPrefix}-${i}`} className="border-ink/15">
        <AccordionTrigger data-testid={`${idPrefix}-trigger-${i}`} className="py-6 text-left text-base font-semibold hover:no-underline sm:text-lg">{f.q}</AccordionTrigger>
        <AccordionContent className="pb-6 pr-8 text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const CtaBand = ({ title = "Make Your Next Digital Launch a Better-Informed Decision.", text = "Tell us about your product, target markets and timeline. We'll respond with a scoped proposal.", testid = "cta-band" }) => (
  <section className="on-dark relative overflow-hidden bg-ink py-24 text-white lg:py-32" data-testid={testid}>
    <img src="/img/review.webp" alt="" width="1400" height="934" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-20" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60" aria-hidden />
    <div className="container-x relative">
      <Reveal className="max-w-4xl">
        <p className="eyebrow mb-6">Next step</p>
        <h2 className="text-4xl font-extrabold leading-[1.02] sm:text-5xl lg:text-6xl">{title}</h2>
        <p className="lede mt-6 max-w-2xl">{text}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/request-assessment" className="btn-primary px-7 py-4 text-base" data-testid={`${testid}-primary`}>Request an Assessment <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/pricing" className="btn-ghost px-7 py-4 text-base" data-testid={`${testid}-secondary`}>View Pricing</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export const TwoCol = ({ label, children }) => (
  <div className="grid gap-6 border-t border-ink/10 py-12 lg:grid-cols-12 lg:gap-12 lg:py-16">
    <h2 className="text-2xl font-extrabold lg:col-span-4 lg:text-3xl">{label}</h2>
    <div className="min-w-0 lg:col-span-8">{children}</div>
  </div>
);

export const DotList = ({ items, cols = 1 }) => (
  <ul className={`grid gap-x-10 gap-y-3 ${cols === 2 ? "sm:grid-cols-2" : ""}`}>
    {items.map((it) => (
      <li key={it} className="flex gap-3 text-[15px] leading-relaxed text-slate-700">
        <CheckCircle2 className="mt-[0.2em] h-5 w-5 flex-none text-teal" aria-hidden />{it}
      </li>
    ))}
  </ul>
);
